import { z } from "zod";
import { Id } from "../common/ids.js";
import { Timestamp } from "../common/time.js";
import { defineRoute } from "../http/route.js";
export const FILE_PURPOSES = [
    "chat",
    "evidence",
    "product_media",
    "order_document",
    "remittance_proof",
    "shipment_document",
    "proof_of_delivery",
    "avatar",
    "invoice",
    "shop_logo",
    "shop_qualification",
    "data_export",
];
export const FilePurpose = z.enum(FILE_PURPOSES).meta({ id: "FilePurpose" });
const IMAGES = ["image/png", "image/jpeg", "image/webp"];
const DOCUMENTS = [
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "text/csv",
    "application/zip",
];
const VIDEO = ["video/mp4", "video/quicktime"];
const MB = 1024 * 1024;
/** Size and type limits per purpose (owner defaults of 2026-09-26); clients read them to check before uploading. */
export const FILE_LIMITS = {
    avatar: { maxBytes: 2 * MB, contentTypes: IMAGES },
    product_media: { maxBytes: 10 * MB, contentTypes: IMAGES },
    chat: { maxBytes: 25 * MB, contentTypes: [...IMAGES, "image/gif", ...DOCUMENTS] },
    order_document: { maxBytes: 25 * MB, contentTypes: [...IMAGES, ...DOCUMENTS] },
    evidence: { maxBytes: 50 * MB, contentTypes: [...IMAGES, "application/pdf", ...VIDEO] },
    remittance_proof: { maxBytes: 50 * MB, contentTypes: [...IMAGES, "application/pdf"] },
    shipment_document: { maxBytes: 25 * MB, contentTypes: [...IMAGES, "application/pdf"] },
    proof_of_delivery: { maxBytes: 25 * MB, contentTypes: [...IMAGES, "application/pdf"] },
    invoice: { maxBytes: 10 * MB, contentTypes: ["application/pdf"] },
    shop_logo: { maxBytes: 2 * MB, contentTypes: IMAGES },
    shop_qualification: { maxBytes: 10 * MB, contentTypes: [...IMAGES, "application/pdf"] },
    // Made by the platform for one person (card 083); nobody uploads one.
    data_export: { maxBytes: 0, contentTypes: [] },
};
/** `removed`: Civentia removed the file from a conversation for a legal reason (card 126); nobody can download it. */
export const FileStatus = z
    .enum(["pending", "scanning", "clean", "rejected", "removed"])
    .meta({ id: "FileStatus" });
export const FileView = z
    .object({
    id: Id,
    purpose: FilePurpose,
    name: z.string(),
    contentType: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    status: FileStatus,
    rejectionReason: z.string().nullable(),
    createdAt: Timestamp,
})
    .meta({ id: "File" });
const Sha256 = z.string().regex(/^[0-9a-f]{64}$/, "Lower-case hex SHA-256");
export const declareUpload = defineRoute({
    id: "files.uploads.create",
    method: "POST",
    path: "/v1/files/uploads",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: ["idem"],
    summary: "Declare a file and get a presigned upload URL valid for 15 minutes; who may upload depends on the purpose, and the owning module decides",
    prd: ["FIL-1"],
    request: {
        body: z.object({
            purpose: FilePurpose,
            name: z.string().trim().min(1).max(200),
            contentType: z.string().min(3).max(120),
            sizeBytes: z.number().int().positive(),
            sha256: Sha256,
            context: z
                .object({ conversationId: Id.optional(), productId: Id.optional(), orderId: Id.optional() })
                .optional()
                .describe("What the file belongs to, for purposes that need it: a `chat` file to a conversation, a `product_media` image to a product, a `remittance_proof`, `shipment_document`, or `proof_of_delivery` to an order"),
        }),
    },
    responses: {
        201: {
            description: "The file, and where and how to PUT its bytes",
            schema: z.object({
                file: FileView,
                upload: z.object({
                    url: z.url(),
                    method: z.literal("PUT"),
                    headers: z.record(z.string(), z.string()).describe("Send exactly these headers with the PUT"),
                    expiresIn: z.number().int().positive(),
                }),
            }),
        },
    },
    errors: [
        "AUTH_REQUIRED",
        "VALIDATION_FAILED",
        "PERMISSION_DENIED",
        "TENANT_REQUIRED",
        "FILE_TOO_LARGE",
        "FILE_TYPE_NOT_ALLOWED",
    ],
});
export const completeUpload = defineRoute({
    id: "files.uploads.complete",
    method: "POST",
    path: "/v1/files/{fileId}/complete",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Confirm the bytes are uploaded; the file is scanned before it becomes visible",
    prd: ["FIL-1"],
    request: { params: z.object({ fileId: Id }) },
    responses: { 200: { description: "The file, now scanning", schema: FileView } },
    errors: ["AUTH_REQUIRED", "NOT_FOUND", "INVALID_TRANSITION"],
});
export const getFile = defineRoute({
    id: "files.get",
    method: "GET",
    path: "/v1/files/{fileId}",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "Metadata and scan status; readable by whoever may read the owning entity",
    prd: ["FIL-1"],
    request: { params: z.object({ fileId: Id }) },
    responses: { 200: { description: "The file", schema: FileView } },
    errors: ["AUTH_REQUIRED", "NOT_FOUND"],
});
export const fileDownloadUrl = defineRoute({
    id: "files.download-url",
    method: "GET",
    path: "/v1/files/{fileId}/download-url",
    surface: "tenant",
    auth: "user",
    permission: "none",
    marks: [],
    summary: "A presigned download URL valid for 5 minutes, only once the scan found the file clean; 410 once Civentia removed it (card 126)",
    prd: ["FIL-1"],
    request: { params: z.object({ fileId: Id }) },
    responses: {
        200: {
            description: "Where to GET the bytes",
            schema: z.object({ url: z.url(), expiresIn: z.number().int().positive() }),
        },
    },
    errors: ["AUTH_REQUIRED", "NOT_FOUND", "FILE_NOT_CLEAN", "FILE_REMOVED"],
});
export const getStorageUsage = defineRoute({
    id: "files.storage.get",
    method: "GET",
    path: "/v1/enterprise/storage",
    surface: "tenant",
    auth: "user",
    permission: "enterprise:manage",
    marks: ["tenant"],
    summary: "The total size of the files the enterprise's members uploaded, which its plan's storage counts (ADR 0023, ENT-7); personal data exports and rejected uploads aside",
    prd: ["ENT-7"],
    responses: {
        200: { description: "Storage used", schema: z.object({ usedBytes: z.number().int().nonnegative() }) },
    },
    errors: ["AUTH_REQUIRED", "TENANT_REQUIRED", "PERMISSION_DENIED"],
});
export const fileRoutes = [declareUpload, completeUpload, getFile, fileDownloadUrl, getStorageUsage];
//# sourceMappingURL=files.routes.js.map